import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const API_URL_KEY = 'api_base_url';

/**
 * Get API Base URL based on stored preference, environment and platform
 *
 * Priority:
 * 1. Stored preference (from Settings page)
 * 2. Environment variable (REACT_APP_API_BASE_URL)
 * 3. Platform-specific defaults (Native vs Web)
 */
const getDefaultApiBaseUrl = () => {
  // 1. Check environment variable
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // 2. Default: Production API
  return 'https://api.billard.flogra.de';
};

// Store the current API base URL (will be updated dynamically)
let currentApiBaseUrl = getDefaultApiBaseUrl();

/**
 * Get API Base URL asynchronously
 * Checks stored preference first, then falls back to defaults
 */
export const getApiBaseUrl = async () => {
  try {
    const { value } = await Preferences.get({ key: API_URL_KEY });
    if (value) {
      console.log('API Base URL from storage:', value);
      currentApiBaseUrl = value;
      return value;
    }
  } catch (error) {
    console.warn('Could not load stored API URL, using default:', error);
  }

  const defaultUrl = getDefaultApiBaseUrl();
  console.log('API Base URL (default):', {
    url: defaultUrl,
    platform: Capacitor.getPlatform(),
    isNative: Capacitor.isNativePlatform(),
  });

  currentApiBaseUrl = defaultUrl;
  return defaultUrl;
};

/**
 * Get the current API Base URL synchronously
 * This returns the last loaded value
 */
export const getCurrentApiBaseUrl = () => {
  return currentApiBaseUrl;
};

/**
 * Update the current API Base URL
 * Called when settings are changed
 */
export const setCurrentApiBaseUrl = (url) => {
  currentApiBaseUrl = url;
  console.log('Updated current API Base URL to:', url);
};

/**
 * Set and persist the API Base URL
 * Used by QR-Code Scanner to set the API URL from scanned data
 */
export const setApiBaseUrl = async (url) => {
  try {
    await Preferences.set({ key: API_URL_KEY, value: url });
    currentApiBaseUrl = url;
    console.log('Persisted API Base URL:', url);
    return true;
  } catch (error) {
    console.error('Failed to persist API URL:', error);
    // Still update the current URL even if persistence fails
    currentApiBaseUrl = url;
    return false;
  }
};

// For backward compatibility - initialize with default
const API_BASE_URL = getDefaultApiBaseUrl();

export default API_BASE_URL;